import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import {
  CheckBadgeIcon,
  DocumentTextIcon,
  FunnelIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { updateUrlWithException } from "../../../../components/Utils";
import { ServiceAgreeementActionEditBar } from "../../../../components/dashboard/service/agreements/action-edit-bar";
import ServiceAgreementMultiEditDialog from "../../../../components/dashboard/service/agreements/multi-edit-dialog";
import { getServiceAgreementsAsync } from "../../../../slices/service/agreements";
import ServiceAgreementModal from "../../../../components/dashboard/service/agreements/modal";
import { ServiceAgreementsTable } from "../../../../components/dashboard/service/agreements/table";

const selectClass =
  "block w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200 sm:min-w-[12rem]";

function parseViewFromFilters(filters) {
  const f = filters?.find((x) => x.name === "view");
  if (!f?.value) return ["ACTIVE"];
  try {
    const p = JSON.parse(f.value);
    return Array.isArray(p) ? p : [p];
  } catch {
    return ["ACTIVE"];
  }
}

export const ServiceAgreementList = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const agreementState = useSelector((store) => store.service_agreements);

  const { isLoading } = agreementState;
  const [selectedItems, setSelectedItems] = useState([]);
  const [controller, setController] = useState({
    filters: [{ name: "view", value: JSON.stringify(["ACTIVE"]) }],
    page: searchParams.get("page") ? searchParams.get("page") : 1,
    per_page: searchParams.get("per_page") ? searchParams.get("per_page") : 25,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "asc",
    sortBy: searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "next_service_date",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
  });

  useEffect(() => {
    document.title = `${t("service.agreements.list.title")} | Promang`;
  }, [t]);

  useEffect(() => {
    updateUrlWithException(
      {
        page: controller.page,
        per_page: controller.per_page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
      },
      "filters"
    );
  }, [controller]);

  const viewFilterOptions = useMemo(
    () => [
      {
        value: "SERVICE_SOON",
        label: t("service.agreements.view.service_soon"),
        class: "bg-amber-100 text-amber-900",
      },
      {
        value: "ACTIVE",
        label: t("service.agreements.view.active"),
        class: "bg-emerald-100 text-emerald-900",
      },
    ],
    [t]
  );

  const customDropdownFilters = useMemo(
    () => [
      {
        key: "view",
        label: t("service.agreements.filter.view"),
        icon: FunnelIcon,
        options: viewFilterOptions,
      },
    ],
    [t, viewFilterOptions]
  );

  const moduleFilterInitial = useMemo(
    () => ({
      query: controller.query || "",
      view: parseViewFromFilters(controller.filters),
    }),
    [controller.query, controller.filters]
  );

  const sortByOptions = useMemo(
    () => [
      {
        name: t("service.agreements.sort.next_service_asc"),
        sort: "asc",
        sortBy: "next_service_date",
      },
      {
        name: t("service.agreements.sort.next_service_desc"),
        sort: "desc",
        sortBy: "next_service_date",
      },
      {
        name: t("service.agreements.sort.created_desc"),
        sort: "desc",
        sortBy: "created_at",
      },
      {
        name: t("service.agreements.sort.created_asc"),
        sort: "asc",
        sortBy: "created_at",
      },
    ],
    [t]
  );

  const currentSortKey = `${controller.sortBy}:${controller.sort}`;

  const handleModuleFiltersChange = (mf) => {
    const raw = mf.view;
    const viewValues =
      Array.isArray(raw) && raw.length > 0 ? raw : ["ACTIVE"];
    setController((prev) => ({
      ...prev,
      page: 1,
      query: mf.query ?? "",
      filters: [{ name: "view", value: JSON.stringify(viewValues) }],
    }));
  };

  const handlePageChange = (newPage) => {
    setController((prev) => ({ ...prev, page: newPage }));
  };

  const handlePerPageChange = (option) => {
    setController((prev) => ({ ...prev, per_page: option, page: 1 }));
  };

  const handleSortChange = (e) => {
    const [sortBy, sort] = e.target.value.split(":");
    setController((prev) => ({
      ...prev,
      sort,
      sortBy,
      page: 1,
    }));
  };

  const debouncedDispatchAction = useMemo(
    () =>
      debounce((ctrl) => {
        dispatch(getServiceAgreementsAsync(ctrl));
      }, 1000),
    [dispatch]
  );

  useEffect(() => {
    debouncedDispatchAction(controller);
    return () => debouncedDispatchAction.cancel();
  }, [controller, debouncedDispatchAction]);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [multiEditDialogOpen, setMultiEditDialogOpen] = useState(false);
  const [priceChangeDialogOpen, setPriceChangeDialogOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 rounded-[2rem] border border-slate-200 bg-white px-6 py-6 shadow-sm">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                <DocumentTextIcon className="h-4 w-4" />
                {t("service.agreements.list.hero_kicker")}
              </div>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
                {t("service.agreements.list.title")}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-600">
                {t("service.agreements.list.subtitle")}
              </p>
            </div>

            <div className="grid w-full max-w-xl grid-cols-2 gap-3 sm:max-w-none sm:grid-cols-3">
              <div className="rounded-2xl bg-slate-950 px-4 py-3 text-white">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-300">
                  {t("service.agreements.list.meta.total")}
                </div>
                <div className="mt-2 text-2xl font-semibold">
                  {agreementState.totalCount ?? 0}
                </div>
              </div>
              <div className="rounded-2xl bg-slate-100 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500">
                  {t("service.agreements.list.meta.new")}
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {agreementState.newCount ?? 0}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditModalOpen(true)}
                className="col-span-2 inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 sm:col-span-1"
              >
                <PlusIcon className="h-5 w-5 shrink-0 text-slate-600" />
                {t("service.agreements.list.new")}
              </button>
            </div>
          </div>
        </div>

        <section className="mb-8 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-6 py-5">
            <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
              {t("service.agreements.list.filters_heading")}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              {t("service.agreements.list.filters_description")}
            </p>
          </div>
          <div className="space-y-5 px-6 py-6">
            <ModuleFilter
              disabled={isLoading}
              hideStatusFilter
              initialFilters={moduleFilterInitial}
              onFiltersChange={handleModuleFiltersChange}
              customDropdownFilters={customDropdownFilters}
              searchPlaceholder={t("service.agreements.list.search_placeholder")}
              selectedItems={selectedItems}
              actionBar={
                <ServiceAgreeementActionEditBar
                  selectedItems={selectedItems}
                  setMultiEditDialogOpen={() => setMultiEditDialogOpen(true)}
                  setPriceChangeDialogOpen={() =>
                    setPriceChangeDialogOpen(true)
                  }
                />
              }
            />

            <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end">
              <label className="block w-full min-w-0 flex-1 lg:max-w-md">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {t("service.agreements.list.sort_label")}
                </span>
                <select
                  className={selectClass}
                  value={currentSortKey}
                  onChange={handleSortChange}
                  disabled={isLoading}
                >
                  {sortByOptions.map((o) => (
                    <option
                      key={`${o.sortBy}-${o.sort}`}
                      value={`${o.sortBy}:${o.sort}`}
                    >
                      {o.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block w-full sm:w-auto">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {t("service.agreements.list.per_page")}
                </span>
                <select
                  className={selectClass}
                  value={String(controller.per_page)}
                  onChange={(e) =>
                    handlePerPageChange(Number(e.target.value))
                  }
                  disabled={isLoading}
                >
                  {[10, 25, 50, 100, 250].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-6 py-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
              <CheckBadgeIcon className="h-4 w-4" />
              {t("service.agreements.list.table_kicker")}
            </div>
            <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
              {t("service.agreements.list.table_title")}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              {t("service.agreements.list.table_description")}
            </p>
          </div>

          <div className="px-4 pb-6 pt-2 sm:px-6">
            <ServiceAgreementsTable
              onItemsSelect={(items) => setSelectedItems(items)}
              itemState={agreementState}
              itemsCount={agreementState.totalCount}
              currentCount={agreementState.currentCount}
              page={controller.page}
              onPageChange={handlePageChange}
              perPage={controller.per_page}
            />
          </div>
        </section>
      </div>

      <ServiceAgreementModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
      />

      <ServiceAgreementMultiEditDialog
        selectedItems={selectedItems}
        open={multiEditDialogOpen}
        onClose={() => setMultiEditDialogOpen(false)}
      />
    </div>
  );
};
